import type { LoginResponse } from '../../types/auth'
import { SESSION_CHECK_ERROR_MESSAGES } from '../../constants/authSession'
import { isNetworkError } from '../../utils/errorUtils'
import { getAuthMe } from '../authApi'
import { isApiHttpError } from '../httpClient'

export type AuthSessionCheckResult =
  | { kind: 'authenticated'; user: LoginResponse }
  | { kind: 'anonymous' }
  | { kind: 'unavailable'; message: string }

function isConfirmedSessionUser(user: unknown): user is LoginResponse {
  if (!user || typeof user !== 'object' || Array.isArray(user)) return false

  const { userId, username } = user as Partial<Record<keyof LoginResponse, unknown>>
  return Number.isInteger(userId)
    && Number(userId) > 0
    && typeof username === 'string'
    && username.length > 0
}

function resolveUnavailableMessage(error: unknown): string {
  if (isApiHttpError(error)) {
    return SESSION_CHECK_ERROR_MESSAGES.httpStatus(error.status)
  }

  if (isNetworkError(error)) {
    return SESSION_CHECK_ERROR_MESSAGES.network
  }

  return SESSION_CHECK_ERROR_MESSAGES.malformedResponse
}

export async function checkAuthSession(apiBaseUrl: string): Promise<AuthSessionCheckResult> {
  let user: unknown
  try {
    user = await getAuthMe(apiBaseUrl)
  } catch (error) {
    if (isApiHttpError(error) && error.status === 401) {
      return { kind: 'anonymous' }
    }

    return { kind: 'unavailable', message: resolveUnavailableMessage(error) }
  }

  if (!isConfirmedSessionUser(user)) {
    return { kind: 'unavailable', message: SESSION_CHECK_ERROR_MESSAGES.malformedResponse }
  }

  return { kind: 'authenticated', user }
}
